import React from 'react';

/* ====================================================================
 * <ScientificFrameworks /> — editorial framework section on tagc.us
 *
 * Lists the scientific and regulatory frameworks Aᴳ engages with in its
 * day-to-day evaluation work. Editorial cards, not bullet vomit — each
 * framework gets a clear what/where/how-it-applies.
 *
 * Pure presentational. No data fetching, no auth. Safe for the public
 * marketing surface.
 * ==================================================================== */

const SLATE = '#0B1426';
const INK = '#1B2538';
const MUTED = '#5B6577';
const CORAL = '#FF4E6A';

const FRAMEWORKS = [
  {
    group: 'Regulatory',
    items: [
      {
        name: 'MoCRA',
        full: 'Modernization of Cosmetics Regulation Act',
        scope: 'U.S. cosmetic facility & product registration, adverse-event reporting, safety-substantiation evidence requirements.',
      },
      {
        name: 'EU Cosmetics Regulation 1223/2009',
        full: 'Regulation (EC) No 1223/2009',
        scope: 'Cosmetic Product Safety Reports (CPSR), responsible-person obligations, Annex II/III/IV/V/VI ingredient controls.',
      },
      {
        name: 'California Proposition 65',
        full: 'Safe Drinking Water and Toxic Enforcement Act',
        scope: 'Exposure-based assessment vs Safe Harbor Levels; warning-decision logic; NSRL / MADL framing.',
      },
      {
        name: 'SCCS Opinions',
        full: 'EU Scientific Committee on Consumer Safety',
        scope: 'Authoritative EU opinions on cosmetic ingredients; SCCS Notes of Guidance methodology baseline.',
      },
      {
        name: 'IFRA Standards',
        full: 'International Fragrance Association',
        scope: 'Fragrance-ingredient use restrictions, dermal sensitization QRA, category-specific limits.',
      },
    ],
  },
  {
    group: 'Scientific',
    items: [
      {
        name: 'Exposure-based risk assessment',
        full: 'SED · MoS · BMD methodology',
        scope: 'Quantitative dermal, oral, inhalation, and mucosal exposure modelling per population band.',
      },
      {
        name: 'Margin of Safety',
        full: 'NOAEL / LOAEL → MoS',
        scope: 'Internal-dose modelling against point-of-departure values; uncertainty factor selection and justification.',
      },
      {
        name: 'Ingredient hazard review',
        full: 'Hazard characterization',
        scope: 'Acute, subchronic, and chronic endpoint review across genotoxicity, sensitization, irritation, repro/dev, and systemic toxicity.',
      },
      {
        name: 'Weight-of-evidence review',
        full: 'WoE evaluation',
        scope: 'Structured integration of human, animal, in-vitro, and in-silico data streams with study-quality weighting.',
      },
      {
        name: 'Scientific substantiation',
        full: 'Claim & label substantiation',
        scope: 'Defensible documentation of safety, performance, and regulatory claims for retailer and litigation contexts.',
      },
    ],
  },
];

export default function ScientificFrameworks() {
  return (
    <section
      id="frameworks"
      className="border-t"
      style={{ borderColor: '#E3D9C0', backgroundColor: '#FBF7EE' }}
      data-testid="tagc-frameworks"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-4">
            <p
              className="text-[11px] font-mono uppercase tracking-[0.3em] mb-4"
              style={{ color: CORAL }}
              data-testid="frameworks-eyebrow"
            >
              Frameworks &amp; methodology
            </p>
            <h2
              className="font-serif text-4xl lg:text-5xl leading-[1.05]"
              style={{ color: SLATE }}
            >
              The science we apply.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
            <p className="text-base lg:text-lg leading-relaxed" style={{ color: MUTED }}>
              Every Aᴳ evaluation references the same regulatory frameworks and
              scientific methodologies a competent authority, retailer, or court
              would reference. We are explicit about which framework applies, where
              its boundary lies, and what evidence the engagement produces.
            </p>
          </div>
        </div>

        {FRAMEWORKS.map((group) => (
          <div key={group.group} className="mb-16 last:mb-0">
            <p
              className="text-[10px] font-mono uppercase tracking-[0.3em] mb-8 pb-3 border-b"
              style={{ color: SLATE, borderColor: '#D6CDB8' }}
              data-testid={`frameworks-group-${group.group.toLowerCase()}`}
            >
              {group.group} frameworks
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
              {group.items.map((f) => (
                <article
                  key={f.name}
                  data-testid={`framework-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <p
                    className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                    style={{ color: CORAL }}
                  >
                    {f.full}
                  </p>
                  <h3
                    className="font-serif text-xl mb-3 leading-tight"
                    style={{ color: SLATE }}
                  >
                    {f.name}
                  </h3>
                  <p
                    className="text-[13.5px] leading-relaxed"
                    style={{ color: MUTED }}
                  >
                    {f.scope}
                  </p>
                </article>
              ))}
            </div>
          </div>
        ))}

        <p
          className="mt-8 text-[12px] font-mono uppercase tracking-[0.22em] pt-6 border-t"
          style={{ color: MUTED, borderColor: '#D6CDB8' }}
          data-testid="frameworks-footer"
        >
          All assessments are signed by a named toxicologist &middot; documented for
          regulatory, retailer, and litigation contexts.
        </p>
      </div>
    </section>
  );
}
